import type { Column, ColumnDef, ColumnMeta } from '@tanstack/react-table';
import { createElement } from 'react';

import type { PinConfig } from '../table-cell/TableCell';
import { formatDecimalPlaces, isNumeric } from '../utils';
import {
  type RevampTableColumn,
  type RevampTableColumnType,
  type RevampTableData,
  type RevampTableKnownStatus,
  type RevampTableRow,
  revampTableKnownStatusTypes,
} from './RevampTable.types';
import { RevampTableCellContent } from './RevampTableCellContent';

const MOBILE_MAX_VISIBLE_ROWS = 5;
const MOBILE_ROW_HEIGHT = 40;
const leftAlignedColumnTypes: RevampTableColumnType[] = [
  'text',
  'status',
  'entity',
];

const isIntegerNumericValue = (value: RevampTableData['value']) =>
  isNumeric(value) && Number.isInteger(Number(value));

export const getRevampTableCellAlignment = (column: RevampTableColumn) =>
  column.alignment ??
  (leftAlignedColumnTypes.includes(column.type) ? 'left' : 'right');

export const isRevampTableColumn = (
  value: unknown
): value is RevampTableColumn => {
  return typeof value === 'object' && value !== null && 'type' in value;
};

export const isNumericColumnType = (type: RevampTableColumnType) => {
  return type === 'number' || type === 'directional';
};

export const getPinnedColumnIds = (columnsMeta: RevampTableColumn[]) => {
  return columnsMeta.filter(column => column.sticky).map(column => column.id);
};

export const getTableMaxHeight = ({
  isTouchDevice,
  rowCount,
}: {
  isTouchDevice: boolean;
  rowCount: number;
}) => {
  if (rowCount > MOBILE_MAX_VISIBLE_ROWS && isTouchDevice) {
    return MOBILE_MAX_VISIBLE_ROWS * MOBILE_ROW_HEIGHT;
  }

  return undefined;
};

export const getColumnPinConfig = (
  column: Column<RevampTableRow>,
  hasHorizontalScroll: boolean
): PinConfig | undefined => {
  if (!hasHorizontalScroll || !column.getIsPinned()) {
    return undefined;
  }

  return {
    left: column.getStart('left'),
    right: column.getStart('right'),
    isLastPinned: column.getIsLastColumn('left'),
  };
};

export const createRevampTableColumns = (
  columnsMeta: RevampTableColumn[]
): ColumnDef<RevampTableRow, RevampTableData>[] =>
  columnsMeta.map(column => ({
    id: column.id,
    header: column.title,
    accessorFn: row => row[column.id],
    cell: ({ getValue }) =>
      createElement(RevampTableCellContent, {
        column,
        data: getValue(),
      }),
    meta: column as ColumnMeta<RevampTableRow, RevampTableData>,
  }));

export const isKnownStatusType = (
  value: unknown
): value is RevampTableKnownStatus => {
  return (
    typeof value === 'string' &&
    revampTableKnownStatusTypes.includes(value as RevampTableKnownStatus)
  );
};

export const getColumnData = ({
  column,
  value,
}: {
  column: RevampTableColumn;
  value: RevampTableData['value'];
}) => {
  if (column.type === 'number') {
    if (isIntegerNumericValue(value)) {
      return value;
    }

    return formatDecimalPlaces(value);
  }

  return value;
};
